import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import type { ExportDestinationContent, ExportTriggerPolicy, FieldContent, Surface } from '@capture/shared';
import { generateSampleBatch, isFormatlessAdapter, remittanceDestinationPrereqs } from '@capture/shared';
import { ADAPTER_OPTIONS } from './destination-options';
import { useExportFormats } from '../../hooks/useExportFormats';
import { ExportFormatWizardPage } from '../../pages/ExportFormatWizardPage';
import { Drawer } from '@/components/Drawer';
import { MappingPanel } from './MappingPanel';
import { StructureBuilder } from './StructureBuilder';
import { StructurePreviewPane } from './components/StructurePreviewPane';
import { BoundStructureView } from './components/BoundStructureView';
import { RoutingPanel, type RoutingFieldSummary } from './RoutingPanel';
import { AdapterPanel } from './AdapterPanel';
import { SchedulePanel } from './SchedulePanel';
import { TriggerPolicyPanel } from './TriggerPolicyPanel';
import styles from './export-composer.module.css';

// F-036 — no 'errors' tab: runtime errors live in the Export History log
// (linked below), keeping this surface configuration-only.
export type DetailTab = 'mapping' | 'structure' | 'routing' | 'adapter' | 'cadence' | 'schedule';

const DETAIL_TABS: DetailTab[] = ['mapping', 'structure', 'routing', 'adapter', 'cadence', 'schedule'];

/** Job-Config variant — per-form field mapping moved off job-level destinations (Plan 3). */
export const JOB_DETAIL_TABS: DetailTab[] = DETAIL_TABS.filter((t) => t !== 'mapping');

interface DestinationDetailProps {
  destination: ExportDestinationContent;
  fields: RoutingFieldSummary[];
  jobTypeId: string;
  /** Job-type processing mode — used to disable batch grouping when mode is 'flow'. Defaults to 'batch'. */
  processingMode?: string;
  /** Job-type-level default trigger policy — shown in the inherit chip. */
  jobTypeDefaultPolicy?: ExportTriggerPolicy | null;
  /**
   * Which sub-tabs to render, in order. Defaults to the full set (`DETAIL_TABS`),
   * preserving the ExportComposer behavior. The Job-Config "Export Destinations"
   * tab passes a set WITHOUT `'mapping'` because per-form field mapping moved off
   * job-level destinations (Plan 3). The initial active tab is `tabs[0]`.
   */
  tabs?: DetailTab[];
  /** Job type surface — enables the transactions-payload prerequisite notice. */
  surface?: Surface;
  onUpdate: (patch: Partial<ExportDestinationContent>) => void;
  onDelete: () => void;
}

export function DestinationDetail({
  destination,
  fields,
  jobTypeId,
  processingMode = 'batch',
  jobTypeDefaultPolicy = null,
  tabs,
  surface,
  onUpdate,
  onDelete,
}: DestinationDetailProps) {
  const detailTabs = tabs ?? DETAIL_TABS;
  const [activeTab, setActiveTab] = useState<DetailTab>(detailTabs[0] ?? 'mapping');

  // R-201 — ExportFormat picker. The bound format (exportFormatId) is what
  // actually drives the export run; the legacy `format` type enum is no longer
  // the editor's primary control.
  const { formats } = useExportFormats();
  const [newFormatOpen, setNewFormatOpen] = useState(false);
  const selectedFormat = useMemo(
    () => formats.find((f) => f.id === destination.exportFormatId) ?? null,
    [formats, destination.exportFormatId],
  );

  // F-037 — formatless adapters (dossier / document_repository) bind no export
  // format and render their own artifact. Pass `formatColumns={undefined}` so
  // MappingPanel runs in free-form add-row mode (map Capture fields → the remote
  // doc-type's keys) instead of the stuck "pick a format first" prompt.
  const formatless = isFormatlessAdapter(destination.adapter);

  // Local copy of the name so the input can be typed in without firing
  // onUpdate on every keystroke. Commits on blur or Enter. Reset when the
  // selected destination changes (parent swaps `destination`).
  const [nameDraft, setNameDraft] = useState(destination.name);
  useEffect(() => {
    setNameDraft(destination.name);
  }, [destination.id, destination.name]);

  function commitName() {
    const trimmed = nameDraft.trim();
    // Empty submission silently reverts to the previous name — restore the
    // input visually so the user sees what stuck.
    if (!trimmed) {
      setNameDraft(destination.name);
      return;
    }
    if (trimmed === destination.name) return;
    onUpdate({ name: trimmed });
  }

  // Sample-data generator needs FieldContent shape. Real-batch override lands in Task 8.
  // stageMap is forwarded when supplied so the preview can evaluate
  // compute@<stage> static behaviors (#53).
  const syntheticFields = useMemo<FieldContent[]>(
    () =>
      fields.map((f, i) => ({
        fieldDefinitionId: f.name,
        name: f.name,
        label: f.label || f.name,
        dataType: f.dataType as FieldContent['dataType'],
        scope: 'record',
        stageMap: f.stageMap ?? {},
        displayOrder: i,
      })),
    [fields]
  );

  const fieldNames = useMemo(() => fields.map((f) => f.name), [fields]);

  // Publish-wall prerequisites for a transactions payload (cheap + pure).
  const prereqs = remittanceDestinationPrereqs(destination, surface ?? 'standard');

  const sampleData = useMemo(() => generateSampleBatch(syntheticFields, 3), [syntheticFields]);

  // StructureBuilder needs both targetColumn AND sourceField per mapping
  // so the picker can preserve the real source on field-node creation (#59).
  const structureMappings = useMemo(
    () =>
      destination.mappings
        .filter((m) => m.targetColumn && m.sourceField)
        .map((m) => ({ targetColumn: m.targetColumn, sourceField: m.sourceField })),
    [destination.mappings]
  );

  function handleDelete() {
    if (!confirm(`Delete destination "${destination.name}"? This cannot be undone.`)) return;
    onDelete();
  }

  return (
    <>
      <div className={styles.detailHeader}>
        <div className={styles.detailIdentity}>
          <input
            type="text"
            className={styles.detailNameInput}
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
            aria-label="Destination name"
            placeholder="Destination name"
          />
          <label className={styles.detailIdentityField}>
            <span className={styles.detailIdentityLabel}>Adapter</span>
            <select
              className={styles.detailIdentitySelect}
              value={destination.adapter}
              onChange={(e) => onUpdate({ adapter: e.target.value as ExportDestinationContent['adapter'] })}
              aria-label="Adapter"
            >
              {ADAPTER_OPTIONS.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </label>
          {/* E-036 — dossier/document_repository build their own envelope/PDF and
              bind no ExportFormat at all; showing the picker invites an operator
              to "configure" a control that does nothing. Hide it entirely for
              those adapters instead of leaving it live-but-inert. */}
          {!formatless && (
            <>
              <label className={styles.detailIdentityField}>
                <span className={styles.detailIdentityLabel}>Export Format</span>
                <select
                  className={styles.detailIdentitySelect}
                  value={destination.exportFormatId ?? ''}
                  onChange={(e) => onUpdate({ exportFormatId: e.target.value || null })}
                  aria-label="Export Format"
                >
                  <option value="">No template — define inline</option>
                  {formats.map((f) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className={styles.formatNewBtn}
                onClick={() => setNewFormatOpen(true)}
              >
                + New format
              </button>
              {selectedFormat ? (
                <span className={styles.formatConfirm} data-testid="selected-format-confirm">
                  {selectedFormat.formatType.replace('_', ' ')} · {selectedFormat.columns.length}{' '}
                  {selectedFormat.columns.length === 1 ? 'column' : 'columns'}
                </span>
              ) : (
                <span className={styles.formatEmpty}>No named format — structure is defined inline below</span>
              )}
            </>
          )}
          <label className={styles.detailIdentityField}>
            <span className={styles.detailIdentityLabel}>Payload</span>
            <select
              className={styles.detailIdentitySelect}
              value={destination.payloadShape ?? 'documents'}
              onChange={(e) =>
                onUpdate({ payloadShape: e.target.value as ExportDestinationContent['payloadShape'] })
              }
              aria-label="Payload"
            >
              <option value="documents">Documents</option>
              <option value="transactions">Transactions</option>
            </select>
          </label>
        </div>
        {/* E-036 — sql/pdf_summary DO bind a format, but each interprets it
            differently; the operator benefits from knowing what it actually
            controls before they wire one up. */}
        {destination.adapter === 'sql' && (
          <p className={styles.formatAdapterHint} data-testid="sql-format-hint">
            A bound format supplies a fixed column list — most sql destinations
            skip it and use <strong>No template — define inline</strong>, defining
            columns directly in the Mapping tab instead.
          </p>
        )}
        {destination.adapter === 'pdf_summary' && (
          <p className={styles.formatAdapterHint} data-testid="pdf-summary-format-hint">
            The format supplies the column list — lengths/delimiters are ignored.
          </p>
        )}
        <div className={styles.detailActions}>
          <button type="button" className={styles.detailActionBtn}>
            Test
          </button>
          <button type="button" className={styles.detailDeleteBtn} onClick={handleDelete}>
            Delete
          </button>
        </div>
      </div>

      {destination.payloadShape === 'transactions' && (
        <div className={styles.payloadPanel}>
          <label className={styles.detailIdentityField}>
            <span className={styles.detailIdentityLabel}>Check images</span>
            <select
              className={styles.detailIdentitySelect}
              value={destination.images ?? 'none'}
              onChange={(e) =>
                onUpdate({ images: e.target.value as ExportDestinationContent['images'] })
              }
              aria-label="Check images"
            >
              <option value="none">None</option>
              <option value="checks">Checks</option>
              <option value="all">All documents</option>
            </select>
          </label>
          <div
            className={styles.payloadPrereqs}
            data-testid="payload-prereqs"
            data-ok={prereqs.surfaceOk && prereqs.policyOk}
            role="status"
            aria-live="polite"
          >
            <div className={styles.prereqRow} data-testid="prereq-surface" data-ok={prereqs.surfaceOk}>
              <span aria-hidden="true">{prereqs.surfaceOk ? '✓' : '✗'}</span>
              <span className={styles.srOnly}>{prereqs.surfaceOk ? 'Met: ' : 'Not met: '}</span>
              <span>
                Remittance-surface job
                {!prereqs.surfaceOk &&
                  ' — a transactions payload can only run on a remittance job type.'}
              </span>
            </div>
            <div className={styles.prereqRow} data-testid="prereq-policy" data-ok={prereqs.policyOk}>
              <span aria-hidden="true">{prereqs.policyOk ? '✓' : '✗'}</span>
              <span className={styles.srOnly}>{prereqs.policyOk ? 'Met: ' : 'Not met: '}</span>
              <span>Accumulate trigger policy grouped by batch (one payment file per batch)</span>
              {!prereqs.policyOk && (
                <button
                  type="button"
                  className={styles.prereqFixBtn}
                  onClick={() =>
                    onUpdate({ triggerPolicy: { mode: 'accumulate', grouping: 'batch' } })
                  }
                >
                  Apply required policy
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <nav className={styles.subTabs} role="tablist">
        {detailTabs.map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={activeTab === tab}
            className={`${styles.subTab}${activeTab === tab ? ` ${styles.subTabActive}` : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
        <Link
          className={styles.runtimeErrorsLink}
          to={`/admin/export-history?destinationId=${destination.id}`}
        >
          Runtime errors →
        </Link>
      </nav>

      <div className={styles.detailContent}>
        {activeTab === 'mapping' && (
          <MappingPanel
            mappings={destination.mappings}
            fieldNames={fieldNames}
            format={destination.format}
            formatColumns={formatless ? undefined : selectedFormat ? selectedFormat.columns : []}
            formatStructure={selectedFormat?.structure ?? undefined}
            boundFormatName={selectedFormat?.name}
            structure={destination.structure}
            onChange={(mappings) => onUpdate({ mappings })}
            sampleData={sampleData}
            jobTypeId={jobTypeId}
            destinationId={destination.id}
            dossierTypeName={
              destination.adapter === 'dossier'
                ? ((destination.config?.detectedTypeName as string) ?? '')
                : undefined
            }
          />
        )}
        {activeTab === 'structure' &&
          (selectedFormat ? (
            // Bound to a named format → structure is owned by the format and shown
            // read-only here (edited under Export Formats). Slice 4b.
            <BoundStructureView
              formatName={selectedFormat.name}
              structure={selectedFormat.structure}
              columns={selectedFormat.columns}
            />
          ) : (
            // Ad-hoc / no template → structure is authored inline on the destination.
            <div className={styles.structureTabPanes}>
              <div className={styles.structureLeftPane}>
                <StructureBuilder
                  structure={destination.structure}
                  onChange={(structure) => onUpdate({ structure })}
                  mappings={structureMappings}
                  format={destination.format}
                />
              </div>
              <div className={styles.structureRightPane}>
                <StructurePreviewPane
                  mappings={destination.mappings}
                  structure={destination.structure}
                  format={destination.format}
                  sampleData={sampleData}
                  widths={destination.config?.widths as Record<string, number> | undefined}
                  columns={formatless ? undefined : []}
                />
              </div>
            </div>
          ))}
        {activeTab === 'routing' && (
          <RoutingPanel
            condition={destination.routingCondition}
            onChange={(condition) => onUpdate({ routingCondition: condition })}
            fields={fields}
          />
        )}
        {activeTab === 'adapter' && (
          <AdapterPanel
            adapterType={destination.adapter}
            config={destination.config}
            onChange={(config) => onUpdate({ config })}
            jobTypeId={jobTypeId}
            destinationId={destination.id}
          />
        )}
        {activeTab === 'cadence' && (
          <TriggerPolicyPanel
            policy={destination.triggerPolicy ?? null}
            inheritedPolicy={jobTypeDefaultPolicy}
            adapterType={destination.adapter}
            processingMode={processingMode}
            onChange={(p) => onUpdate({ triggerPolicy: p })}
          />
        )}
        {activeTab === 'schedule' && (
          <SchedulePanel
            schedule={destination.schedule}
            onChange={(schedule) => onUpdate({ schedule })}
          />
        )}
      </div>

      <Drawer
        isOpen={newFormatOpen}
        onClose={() => setNewFormatOpen(false)}
        title="New export format"
        width="lg"
      >
        <ExportFormatWizardPage
          embedded
          onCreated={(fmt) => {
            onUpdate({ exportFormatId: fmt.id });
            setNewFormatOpen(false);
          }}
        />
      </Drawer>
    </>
  );
}
